import type {
  ArithOp,
  CompareOp,
  Expr,
  LogicOp,
  PrintEnd,
  Program,
  ReadKind,
  ScalarType,
  Stmt,
} from '../ir.js';
import { stmtExprs, walkExpr, walkStmts } from '../ir.js';
import { inferTypes, typeOf } from '../types.js';
import type { LineRange } from '../writer.js';
import { lexJs, JsLexError, type JsToken } from './jslex.js';
import { sliceLength } from './slice.js';
import type { Span, TextError, TextProgram } from './source.js';

const COMPARE: Record<string, CompareOp> = {
  '===': '==',
  '==': '==',
  '!==': '!=',
  '!=': '!=',
  '<': '<',
  '<=': '<=',
  '>': '>',
  '>=': '>=',
};

const READ_FN: Record<string, ReadKind> = {
  readNumber: 'int',
  readDecimal: 'double',
  readText: 'text',
};

/** Words that start a declaration; PebbleCode gives all three the same meaning. */
const DECLARE = ['let', 'const', 'var'];

/** JavaScript we deliberately do not support yet; naming them makes the error useful. */
/** Text methods a block stands behind; `slice` and `substring` are the same piece of text. */
const TEXT_METHODS = new Set([
  'toUpperCase',
  'toLowerCase',
  'charCodeAt',
  'indexOf',
  'slice',
  'substring',
  'substr',
]);

const UNSUPPORTED_WORDS = new Set([
  'function',
  'return',
  'do',
  'switch',
  'case',
  'continue',
  'class',
  'new',
  'delete',
  'typeof',
  'instanceof',
  'try',
  'catch',
  'finally',
  'throw',
  'import',
  'export',
  'async',
  'await',
  'yield',
  'of',
  'in',
  'null',
  'undefined',
  'alert',
  'prompt',
  'parseInt',
  'parseFloat',
  'JSON',
  // `Array` is only ever read by `arrayInit`, which looks at the tokens before this does.
  'Array',
]);

class Fail extends Error {
  constructor(readonly info: TextError) {
    super(info.code);
  }
}

/**
 * The JavaScript a PebbleCode student writes: everything the JavaScript view generates, plus the
 * ordinary ways the same program is written by hand (`var`/`const`, `==`, `x += 1`, comments,
 * a semicolon left off at the end of a line). Anything else is reported with a line and a reason
 * rather than guessed at.
 *
 * Unlike C++ there are no declared types to consult, which makes division simple — `/` is always
 * real, `Math.trunc(a / b)` is the whole-number one — and makes `+` the only ambiguity, resolved
 * by `textVars` below.
 */
class JsParser {
  private i = 0;
  private toks: JsToken[];
  private readonly lineMap: Record<string, LineRange> = {};
  private readonly headMap: Record<string, LineRange> = {};
  private readonly spans: Record<string, Span> = {};
  /** Where each `read` was written, so `checkReads` can point at it after the fact. */
  private readonly readAt = new Map<string, JsToken>();
  /** Ids of `x.charCodeAt(0)` codes: on a whole text they mean its first character. */
  private readonly charCodeAt0 = new Set<string>();
  private exprCount = 0;

  constructor(
    private readonly source: string,
    toks: JsToken[],
  ) {
    this.toks = toks;
  }

  // ---------- token helpers ----------

  private get tok(): JsToken {
    return this.toks[this.i] ?? this.toks[this.toks.length - 1]!;
  }

  /** A method, not a comparison on `tok`, so narrowing never leaks between loops. */
  private atEnd(): boolean {
    return this.toks[this.i]?.kind === 'eof';
  }

  /** A string's `text` is its decoded value, which may well be ";" — never an operator match. */
  private at(text: string): boolean {
    const t = this.tok;
    return t.text === text && t.kind !== 'string' && t.kind !== 'template';
  }

  private atAny(...texts: string[]): boolean {
    return texts.some((t) => this.at(t));
  }

  private next(): JsToken {
    const t = this.tok;
    if (this.i < this.toks.length - 1) this.i++;
    return t;
  }

  private eat(text: string): boolean {
    if (!this.at(text)) return false;
    this.i++;
    return true;
  }

  private expect(text: string): JsToken {
    if (!this.at(text)) this.fail('expected', { expected: text, found: this.tok.text || 'end' });
    return this.next();
  }

  /** The token just consumed; the end of the statement being built. */
  private get prev(): JsToken {
    return this.toks[this.i - 1] ?? this.tok;
  }

  /**
   * JavaScript lets the semicolon be left off at the end of a line (ASI). We accept exactly that:
   * a newline, a closing brace, or the end of the file. `let a = 1 print(a)` on one line is still
   * an error, which is what the student needs to see.
   */
  private semicolon(): JsToken {
    const before = this.prev;
    if (this.at(';')) return this.next();
    if (this.atEnd() || this.at('}') || this.tok.line > before.line) return before;
    return this.fail('expected', { expected: ';', found: this.tok.text || 'end' });
  }

  private fail(code: TextError['code'], params?: Record<string, string>): never {
    return this.failAt(this.tok, code, params);
  }

  /** The same, for a mistake only noticed later: it still points at where it was written. */
  private failAt(t: JsToken, code: TextError['code'], params?: Record<string, string>): never {
    throw new Fail({
      code,
      line: t.line,
      column: t.column,
      span: { from: t.from, to: Math.max(t.to, t.from + 1) },
      ...(params ? { params } : {}),
    });
  }

  // ---------- ids and maps ----------

  /**
   * Statement ids are a pure function of the parse tree, exactly as in the C++ parser: the server
   * re-parses the same text on its own and has to mint the very same ids.
   */
  private stmtId(path: string): string {
    return `t:${path}`;
  }

  private childId(path: string, index: number): string {
    return this.stmtId(path === '' ? String(index) : `${path}.${index}`);
  }

  private childPath(path: string, index: number, tag = ''): string {
    return path === '' ? `${index}${tag}` : `${path}.${index}${tag}`;
  }

  private exprId(stmtId: string): string {
    return `${stmtId}/e${++this.exprCount}`;
  }

  private mark(id: string, start: JsToken, endTok: JsToken, headEnd = endTok): void {
    this.lineMap[id] = [start.line, endTok.line];
    this.headMap[id] = [start.line, headEnd.line];
    this.spans[id] = { from: start.from, to: endTok.to };
  }

  private markExpr(e: Expr, start: JsToken, end: JsToken): void {
    this.lineMap[e.id] ??= [start.line, end.line];
    this.spans[e.id] ??= { from: start.from, to: end.to };
  }

  // ---------- statements ----------

  /** A JavaScript file has no `int main()`; the whole file is the program. */
  parse(): Stmt[] {
    const out: Stmt[] = [];
    while (!this.atEnd()) this.statement('', out);
    return out;
  }

  private statements(path: string, end: string): Stmt[] {
    const out: Stmt[] = [];
    while (!this.at(end) && !this.atEnd()) this.statement(path, out);
    return out;
  }

  /** One written statement; it may produce several IR statements (`let a = 1, b = 2;`). */
  private statement(path: string, out: Stmt[]): void {
    const start = this.tok;
    const id = this.childId(path, out.length);

    if (start.kind === 'ident' && UNSUPPORTED_WORDS.has(start.text)) {
      this.fail('unsupported', { what: start.text });
    }
    if (this.eat(';')) return;
    if (this.at('{')) {
      // A bare block: its statements simply join the surrounding list.
      this.next();
      while (!this.at('}') && !this.atEnd()) this.statement(path, out);
      this.expect('}');
      return;
    }
    if (this.at('break')) {
      this.next();
      const end = this.semicolon();
      out.push({ k: 'break', id });
      this.mark(id, start, end);
      return;
    }
    if (this.at('if')) return this.ifStatement(id, path, out, start);
    if (this.at('while')) return this.whileStatement(id, path, out, start);
    if (this.at('for')) return this.forStatement(id, path, out, start);
    if (this.atAny(...DECLARE)) return this.declaration(path, out, start);
    if (this.printName() !== null) return this.printStatement(id, out, start);
    return this.assignment(id, out, start);
  }

  /** `print(`, `write(` or `console.log(` at the cursor, or null. */
  private printName(): 'print' | 'write' | 'console.log' | null {
    const t = this.tok;
    if (t.kind !== 'ident') return null;
    if ((t.text === 'print' || t.text === 'write') && this.toks[this.i + 1]?.text === '(') {
      return t.text;
    }
    if (
      t.text === 'console' &&
      this.toks[this.i + 1]?.text === '.' &&
      this.toks[this.i + 2]?.text === 'log' &&
      this.toks[this.i + 3]?.text === '('
    ) {
      return 'console.log';
    }
    return null;
  }

  private declaration(path: string, out: Stmt[], start: JsToken): void {
    this.next();
    do {
      if (this.tok.kind !== 'ident') {
        this.fail('expected', { expected: 'a name', found: this.tok.text || 'end' });
      }
      const nameTok = this.next();
      // `let a, b;` is the generator's hoisted line: it names variables but sets nothing.
      if (this.eat('=')) {
        const id = this.childId(path, out.length);
        const made = this.arrayInit(id);
        out.push(
          made
            ? { k: 'newArray', id, name: nameTok.text, ...made, base: 0 }
            : { k: 'set', id, name: nameTok.text, value: this.expression(id) },
        );
        this.mark(id, start, this.prev);
      }
    } while (this.eat(','));
    this.semicolon();
  }

  private assignment(id: string, out: Stmt[], start: JsToken): void {
    if (this.atAny('++', '--')) {
      const op = this.next().text;
      if (this.tok.kind !== 'ident') {
        this.fail('expected', { expected: 'a name', found: this.tok.text || 'end' });
      }
      const nameTok = this.next();
      const end = this.semicolon();
      out.push(this.changeBy(id, nameTok.text, op === '++' ? 1 : -1));
      this.mark(id, start, end);
      return;
    }
    if (this.tok.kind !== 'ident') {
      this.fail('expected', { expected: 'a name', found: this.tok.text || 'end' });
    }
    const name = this.next().text;

    if (this.at('[')) return this.setAtStatement(id, out, start, name);
    if (this.atAny('++', '--')) {
      const op = this.next().text;
      const end = this.semicolon();
      out.push(this.changeBy(id, name, op === '++' ? 1 : -1));
      this.mark(id, start, end);
      return;
    }
    if (this.atAny('+=', '-=', '*=', '/=', '%=')) {
      const op = this.next().text;
      const value = this.expression(id);
      const end = this.semicolon();
      if (op === '+=' || op === '-=') {
        const delta: Expr =
          op === '+='
            ? value
            : {
                k: 'arith',
                id: this.exprId(id),
                op: '-',
                left: this.num(id, 0),
                right: value,
              };
        out.push({ k: 'change', id, name, delta });
      } else {
        const arithOp: ArithOp = op === '*=' ? '*' : op === '/=' ? '/' : 'mod';
        out.push({
          k: 'set',
          id,
          name,
          value: {
            k: 'arith',
            id: this.exprId(id),
            op: arithOp,
            left: { k: 'var', id: this.exprId(id), name },
            right: value,
          },
        });
      }
      this.mark(id, start, end);
      return;
    }
    this.expect('=');
    const made = this.arrayInit(id);
    const value = made ? null : this.expression(id);
    const end = this.semicolon();
    out.push(
      value === null
        ? { k: 'newArray', id, name, ...made!, base: 0 }
        : { k: 'set', id, name, value },
    );
    this.mark(id, start, end);
  }

  /** `a[i] = v`, and the shorthands `a[i] += v` and `a[i]++` that mean the same thing. */
  private setAtStatement(id: string, out: Stmt[], start: JsToken, name: string): void {
    const { index, index2 } = this.indexes(id);
    // The shorthands read the place as well as write it, and a read needs its own nodes.
    const place = (): Expr => ({
      k: 'at',
      id: this.exprId(id),
      name,
      index: this.clone(index, id),
      index2: index2 ? this.clone(index2, id) : null,
    });
    let value: Expr;
    if (this.atAny('++', '--')) {
      const op = this.next().text;
      value = {
        k: 'arith',
        id: this.exprId(id),
        op: op === '++' ? '+' : '-',
        left: place(),
        right: this.num(id, 1),
      };
    } else if (this.atAny('+=', '-=', '*=', '/=', '%=')) {
      const op = this.next().text;
      const right = this.expression(id);
      const arithOp: ArithOp =
        op === '+=' ? '+' : op === '-=' ? '-' : op === '*=' ? '*' : op === '/=' ? '/' : 'mod';
      value = { k: 'arith', id: this.exprId(id), op: arithOp, left: place(), right };
    } else {
      this.expect('=');
      value = this.expression(id);
    }
    const end = this.semicolon();
    out.push({ k: 'setAt', id, name, index, index2, value });
    this.mark(id, start, end);
  }

  /** `[i]` or `[i][j]`: the blocks have lists and tables, and nothing deeper. */
  private indexes(id: string): { index: Expr; index2: Expr | null } {
    this.expect('[');
    const index = this.expression(id);
    this.expect(']');
    let index2: Expr | null = null;
    if (this.eat('[')) {
      index2 = this.expression(id);
      this.expect(']');
    }
    if (this.at('[')) this.fail('unsupported', { what: 'a list inside a table' });
    return { index, index2 };
  }

  /** A fresh copy of an expression: every node in the program needs an id of its own. */
  private clone(e: Expr, id: string): Expr {
    const copy: Expr = { ...e, id: this.exprId(id) };
    switch (copy.k) {
      case 'arith':
      case 'compare':
      case 'logic':
      case 'join':
        return { ...copy, left: this.clone(copy.left, id), right: this.clone(copy.right, id) };
      case 'not':
      case 'length':
      case 'code':
      case 'chr':
      case 'case':
        return { ...copy, arg: this.clone(copy.arg, id) };
      case 'find':
        return { ...copy, needle: this.clone(copy.needle, id), hay: this.clone(copy.hay, id) };
      case 'substr':
        return {
          ...copy,
          arg: this.clone(copy.arg, id),
          from: this.clone(copy.from, id),
          len: this.clone(copy.len, id),
        };
      case 'at':
        return {
          ...copy,
          index: this.clone(copy.index, id),
          index2: copy.index2 ? this.clone(copy.index2, id) : null,
        };
      default:
        return copy;
    }
  }

  /**
   * A list or table being made. It is only ever the right-hand side of an assignment, which is
   * also the only place the blocks can make one.
   *
   * `base` is always 0: JavaScript counts from zero and the text carries no other numbering. A
   * list the blocks numbered from 1 is written `new Array(n + 1)` and comes back as a 0-based
   * list of n + 1 places — the same places, named the way JavaScript names them.
   */
  private arrayInit(id: string): { elem: ScalarType; dims: [Expr] | [Expr, Expr] } | null {
    if (this.at('[')) {
      this.next();
      // `[]` is an empty list; `[1, 2, 3]` would need statements the blocks never had.
      if (!this.at(']')) this.fail('unsupported', { what: 'a list written out in full' });
      this.next();
      return { elem: 'int', dims: [this.num(id, 0)] };
    }
    if (this.at('new')) {
      const made = this.newArrayCall(id);
      return { elem: made.elem, dims: [made.size] };
    }
    if (this.at('Array') && this.toks[this.i + 1]?.text === '.') {
      this.next();
      this.next();
      if (!this.at('from')) this.fail('unsupported', { what: `Array.${this.tok.text}` });
      this.next();
      this.expect('(');
      this.expect('{');
      if (!this.at('length')) this.fail('expected', { expected: 'length', found: this.tok.text });
      this.next();
      this.expect(':');
      const rows = this.expression(id);
      this.eat(',');
      this.expect('}');
      this.expect(',');
      this.expect('(');
      this.expect(')');
      this.expect('=>');
      const inner = this.newArrayCall(id);
      this.expect(')');
      return { elem: inner.elem, dims: [rows, inner.size] };
    }
    return null;
  }

  /** `new Array(n)` with an optional `.fill(…)`; the fill is what names the element type. */
  private newArrayCall(id: string): { elem: ScalarType; size: Expr } {
    this.expect('new');
    if (!this.at('Array')) this.fail('unsupported', { what: `new ${this.tok.text || 'end'}` });
    this.next();
    this.expect('(');
    const size = this.expression(id);
    this.expect(')');
    let elem: ScalarType = 'int';
    if (this.at('.')) {
      this.next();
      if (!this.at('fill')) this.fail('unsupported', { what: `Array.${this.tok.text}` });
      this.next();
      this.expect('(');
      elem = this.zeroType();
      this.expect(')');
    }
    return { elem, size };
  }

  /** Every place of a new list starts empty — 0, "" or false — and that says what it holds. */
  private zeroType(): ScalarType {
    const t = this.tok;
    if (t.kind === 'number' && Number(t.text) === 0) {
      this.next();
      return 'int';
    }
    if (t.kind === 'string' && t.text === '') {
      this.next();
      return 'string';
    }
    if (t.kind === 'ident' && t.text === 'false') {
      this.next();
      return 'bool';
    }
    return this.fail('unsupported', { what: 'a list filled with something other than zero' });
  }

  private changeBy(id: string, name: string, by: number): Stmt {
    return { k: 'change', id, name, delta: this.num(id, by) };
  }

  /**
   * `print(x)` ends the line, `write(x)` does not and `write(x, " ")` ends it with a space — the
   * three shapes the generator emits. `console.log` is the spelling students bring with them.
   */
  private printStatement(id: string, out: Stmt[], start: JsToken): void {
    const helper = this.printName()!;
    this.next();
    if (helper === 'console.log') {
      this.next();
      this.next();
    }
    this.expect('(');
    const parts: Expr[] = [];
    if (!this.at(')')) {
      do {
        const from = this.tok;
        const e = this.expression(id);
        this.markExpr(e, from, this.prev);
        parts.push(e);
      } while (this.eat(','));
    }
    this.expect(')');
    const end = this.semicolon();

    let printEnd: PrintEnd = helper === 'write' ? 'none' : 'newline';
    if (helper === 'write') {
      // A trailing " " or "\n" is how the inline print block says how its line ends.
      const last = parts[parts.length - 1];
      if (last?.k === 'text' && (last.value === ' ' || last.value === '\n')) {
        printEnd = last.value === ' ' ? 'space' : 'newline';
        parts.pop();
      }
    } else if (helper === 'console.log') {
      // console.log(a, b) puts a space between its arguments; keep that visible in the program.
      for (let n = parts.length - 1; n > 0; n--) parts.splice(n, 0, this.text(id, ' '));
    }
    const value =
      parts.length === 0
        ? this.text(id, '')
        : parts.reduce((a, b) => ({ k: 'join', id: this.exprId(id), left: a, right: b }));
    out.push({ k: 'print', id, value, end: printEnd });
    this.mark(id, start, end);
  }

  private ifStatement(id: string, path: string, out: Stmt[], start: JsToken): void {
    this.next();
    this.expect('(');
    const cond = this.expression(id);
    const close = this.expect(')');
    const branches: { cond: Expr; body: Stmt[] }[] = [];
    const body = this.block(this.childPath(path, out.length, 'b0'));
    branches.push({ cond, body: body.body });
    let orElse: Stmt[] | null = null;
    let lastTok = body.end;
    while (this.at('else')) {
      this.next();
      if (this.at('if')) {
        this.next();
        this.expect('(');
        const c = this.expression(id);
        this.expect(')');
        const b = this.block(this.childPath(path, out.length, `b${branches.length}`));
        branches.push({ cond: c, body: b.body });
        lastTok = b.end;
      } else {
        const b = this.block(this.childPath(path, out.length, 'e'));
        orElse = b.body;
        lastTok = b.end;
        break;
      }
    }
    out.push({ k: 'if', id, branches, orElse });
    this.mark(id, start, lastTok, close);
  }

  private whileStatement(id: string, path: string, out: Stmt[], start: JsToken): void {
    this.next();
    this.expect('(');
    const cond = this.expression(id);
    const close = this.expect(')');
    const body = this.block(this.childPath(path, out.length));
    out.push({ k: 'while', id, cond, body: body.body });
    this.mark(id, start, body.end, close);
  }

  private forStatement(id: string, path: string, out: Stmt[], start: JsToken): void {
    this.next();
    this.expect('(');
    const declaredHere = this.atAny(...DECLARE);
    if (declaredHere) this.next();
    if (this.tok.kind !== 'ident') {
      this.fail('expected', { expected: 'a name', found: this.tok.text || 'end' });
    }
    const name = this.next().text;
    // `for (const x of list)` reads a list, which the blocks have no counting loop for.
    if (this.atAny('of', 'in')) this.fail('unsupported', { what: `for … ${this.tok.text}` });
    this.expect('=');
    const from = this.expression(id);
    this.expect(';');
    // The condition tells us the direction; only `name <= to` / `name >= to` are supported.
    if (this.tok.kind !== 'ident' || this.tok.text !== name)
      this.fail('unsupported', { what: 'for' });
    this.next();
    const cmp = this.next().text;
    if (cmp !== '<=' && cmp !== '>=' && cmp !== '<' && cmp !== '>') {
      this.fail('unsupported', { what: 'for' });
    }
    let to = this.expression(id);
    if (cmp === '<')
      to = { k: 'arith', id: this.exprId(id), op: '-', left: to, right: this.num(id, 1) };
    if (cmp === '>')
      to = { k: 'arith', id: this.exprId(id), op: '+', left: to, right: this.num(id, 1) };
    this.expect(';');
    const down = cmp === '>=' || cmp === '>';
    this.forStep(name, down);
    const close = this.expect(')');
    const body = this.block(this.childPath(path, out.length));

    // `for (let k = 1; k <= n; k++)` whose counter is never read is the "repeat n times" block.
    const usesName = body.body.some((s) =>
      [...walkStmts([s])].some((x) =>
        stmtExprs(x).some((e) => [...walkExpr(e)].some((n) => n.k === 'var' && n.name === name)),
      ),
    );
    if (declaredHere && !usesName && !down && from.k === 'num' && from.value === 1) {
      out.push({ k: 'repeat', id, times: to, body: body.body });
    } else {
      out.push({ k: 'for', id, name, from, to, down, body: body.body });
    }
    this.mark(id, start, body.end, close);
  }

  /** The step: name++ / ++name / name-- / --name / name += 1 / name -= 1. */
  private forStep(name: string, down: boolean): void {
    const want = down ? '--' : '++';
    if (this.atAny('++', '--')) {
      if (this.next().text !== want) this.fail('unsupported', { what: 'for' });
      if (this.tok.kind === 'ident' && this.tok.text === name) this.next();
      return;
    }
    if (this.tok.kind === 'ident' && this.tok.text === name) {
      this.next();
      if (this.atAny('++', '--')) {
        if (this.next().text !== want) this.fail('unsupported', { what: 'for' });
        return;
      }
      if (this.atAny('+=', '-=')) {
        const op = this.next().text;
        const step = this.tok;
        if (step.kind !== 'number' || step.text !== '1' || (op === '+=') === down) {
          this.fail('unsupported', { what: 'for' });
        }
        this.next();
        return;
      }
    }
    this.fail('unsupported', { what: 'for' });
  }

  /** A `{ … }` block or a single statement after if/while/for. */
  private block(path: string): { body: Stmt[]; end: JsToken } {
    if (this.eat('{')) {
      const body = this.statements(path, '}');
      const end = this.expect('}');
      return { body, end };
    }
    const body: Stmt[] = [];
    this.statement(path, body);
    return { body, end: this.prev };
  }

  // ---------- expressions ----------

  private num(id: string, value: number): Expr {
    return { k: 'num', id: this.exprId(id), value };
  }

  private text(id: string, value: string): Expr {
    return { k: 'text', id: this.exprId(id), value };
  }

  private expression(id: string): Expr {
    return this.orExpr(id);
  }

  private binary(
    id: string,
    next: (id: string) => Expr,
    ops: string[],
    make: (op: string, left: Expr, right: Expr) => Expr,
  ): Expr {
    let left = next(id);
    while (this.tok.kind === 'op' && ops.includes(this.tok.text)) {
      const start = this.tok;
      const op = this.next().text;
      const right = next(id);
      left = make(op, left, right);
      this.markExpr(left, start, this.prev);
    }
    return left;
  }

  private orExpr(id: string): Expr {
    return this.binary(
      id,
      (x) => this.andExpr(x),
      ['||'],
      (_op, left, right) => ({ k: 'logic', id: this.exprId(id), op: 'or' as LogicOp, left, right }),
    );
  }

  private andExpr(id: string): Expr {
    return this.binary(
      id,
      (x) => this.eqExpr(x),
      ['&&'],
      (_op, left, right) => ({
        k: 'logic',
        id: this.exprId(id),
        op: 'and' as LogicOp,
        left,
        right,
      }),
    );
  }

  private eqExpr(id: string): Expr {
    return this.binary(
      id,
      (x) => this.relExpr(x),
      ['===', '!==', '==', '!='],
      (op, left, right) => ({ k: 'compare', id: this.exprId(id), op: COMPARE[op]!, left, right }),
    );
  }

  private relExpr(id: string): Expr {
    return this.binary(
      id,
      (x) => this.addExpr(x),
      ['<', '<=', '>', '>='],
      (op, left, right) => ({ k: 'compare', id: this.exprId(id), op: COMPARE[op]!, left, right }),
    );
  }

  private addExpr(id: string): Expr {
    return this.binary(
      id,
      (x) => this.mulExpr(x),
      ['+', '-'],
      (op, left, right) => ({ k: 'arith', id: this.exprId(id), op: op as ArithOp, left, right }),
    );
  }

  private mulExpr(id: string): Expr {
    return this.binary(
      id,
      (x) => this.unary(x),
      ['*', '/', '%'],
      // `/` in JavaScript is always real division; `Math.trunc(a / b)` is the whole-number one.
      (op, left, right) => ({
        k: 'arith',
        id: this.exprId(id),
        op: op === '%' ? 'mod' : (op as ArithOp),
        left,
        right,
      }),
    );
  }

  private unary(id: string): Expr {
    if (this.at('!')) {
      const start = this.next();
      const arg = this.unary(id);
      const e: Expr = { k: 'not', id: this.exprId(id), arg };
      this.markExpr(e, start, this.prev);
      return e;
    }
    if (this.at('-')) {
      const start = this.next();
      const arg = this.unary(id);
      // `-1` is one literal; `0 - n` stays the subtraction the student (or the generator) wrote.
      if (arg.k === 'num') return { ...arg, value: -arg.value };
      const e: Expr = {
        k: 'arith',
        id: this.exprId(id),
        op: '-',
        left: this.num(id, 0),
        right: arg,
      };
      this.markExpr(e, start, this.prev);
      return e;
    }
    if (this.eat('+')) return this.unary(id);
    return this.postfix(id);
  }

  private postfix(id: string): Expr {
    let e = this.primary(id);
    for (;;) {
      if (this.at('[')) {
        const start = this.tok;
        // Only a named list has places; `readText()[0]` is not something the blocks can say.
        if (e.k !== 'var')
          this.fail('unsupported', { what: 'indexing something that is not a list' });
        const { index, index2 } = this.indexes(id);
        e = { k: 'at', id: this.exprId(id), name: e.name, index, index2 };
        this.markExpr(e, start, this.prev);
        continue;
      }
      if (this.at('.') && this.toks[this.i + 1]?.text === 'length') {
        const start = this.tok;
        this.next();
        const end = this.next();
        // Whether this counts letters or places is settled once the whole program is in hand.
        e = { k: 'length', id: this.exprId(id), arg: e };
        this.markExpr(e, start, end);
        continue;
      }
      const method = this.at('.') ? this.toks[this.i + 1]?.text : undefined;
      if (method !== undefined && TEXT_METHODS.has(method)) {
        const start = this.tok;
        this.next();
        this.next();
        e = this.textMethod(id, method, e);
        this.markExpr(e, start, this.prev);
        continue;
      }
      return e;
    }
  }

  /** `.charCodeAt`, `.toUpperCase`, `.indexOf`, `.slice` and friends, after the dot. */
  private textMethod(id: string, method: string, target: Expr): Expr {
    this.expect('(');
    const args: Expr[] = [];
    while (!this.at(')')) {
      args.push(this.expression(id));
      if (!this.eat(',')) break;
    }
    this.expect(')');
    const arity = (n: number, m = n) => {
      if (args.length < n || args.length > m) this.fail('unsupported', { what: `.${method}()` });
    };
    switch (method) {
      case 'toUpperCase':
      case 'toLowerCase':
        arity(0);
        return { k: 'case', id: this.exprId(id), upper: method === 'toUpperCase', arg: target };
      case 'charCodeAt': {
        arity(1);
        const at = args[0]!;
        if (at.k === 'num' && at.value === 0) {
          const e: Expr = { k: 'code', id: this.exprId(id), arg: target };
          this.charCodeAt0.add(e.id);
          return e;
        }
        // `s.charCodeAt(i)` is the code of `s[i]`.
        if (target.k !== 'var') this.fail('unsupported', { what: '.charCodeAt()' });
        return {
          k: 'code',
          id: this.exprId(id),
          arg: { k: 'at', id: this.exprId(id), name: target.name, index: at, index2: null },
        };
      }
      case 'indexOf':
        arity(1);
        return { k: 'find', id: this.exprId(id), needle: args[0]!, hay: target };
      case 'substr':
        arity(2);
        return { k: 'substr', id: this.exprId(id), arg: target, from: args[0]!, len: args[1]! };
      default: {
        // slice / substring: where the piece starts and where it stops (or the end of the text).
        arity(1, 2);
        const from = args[0]!;
        const len: Expr =
          args[1] !== undefined
            ? sliceLength(from, args[1], () => this.exprId(id))
            : {
                k: 'arith',
                id: this.exprId(id),
                op: '-',
                left: { k: 'length', id: this.exprId(id), arg: this.clone(target, id) },
                right: this.clone(from, id),
              };
        return { k: 'substr', id: this.exprId(id), arg: target, from, len };
      }
    }
  }

  private primary(id: string): Expr {
    const t = this.tok;
    if (t.kind === 'number') {
      this.next();
      const e = this.num(id, Number(t.text));
      this.markExpr(e, t, t);
      return e;
    }
    if (t.kind === 'string') {
      this.next();
      const e = this.text(id, t.text);
      this.markExpr(e, t, t);
      return e;
    }
    if (t.kind === 'template') return this.template(id, t);
    if (t.kind === 'ident') return this.identExpr(id, t);
    if (this.at('(')) {
      this.next();
      const inner = this.expression(id);
      if (this.at('?')) this.fail('unsupported', { what: '?:' });
      this.expect(')');
      return inner;
    }
    return this.fail('expected', { expected: 'a value', found: t.text || 'end' });
  }

  /** A template literal is the generator's way of writing a join; each `${…}` is one part. */
  private template(id: string, t: JsToken): Expr {
    this.next();
    const parts: Expr[] = [];
    for (const chunk of t.chunks ?? []) {
      // An empty piece (between two neighbouring `${…}`) joins nothing and is left out.
      if (chunk.text !== '') parts.push(this.text(id, chunk.text));
      if (chunk.expr) parts.push(this.subExpression(id, chunk.expr));
    }
    if (parts.length === 0) return this.text(id, '');
    const e = parts.reduce((a, b) => ({ k: 'join', id: this.exprId(id), left: a, right: b }));
    this.markExpr(e, t, t);
    return e;
  }

  /**
   * Parses one `${…}`. Its tokens are lexed from the original source on demand, so they keep
   * their real lines and offsets; the cursor is put back where it was afterwards.
   */
  private subExpression(id: string, span: Span): Expr {
    const outerToks = this.toks;
    const outerI = this.i;
    this.toks = lexJs(this.source, span.from, span.to);
    this.i = 0;
    try {
      const e = this.expression(id);
      if (!this.atEnd()) this.fail('expected', { expected: '}', found: this.tok.text });
      return e;
    } finally {
      this.toks = outerToks;
      this.i = outerI;
    }
  }

  private identExpr(id: string, t: JsToken): Expr {
    if (t.text === 'true' || t.text === 'false') {
      this.next();
      const e: Expr = { k: 'bool', id: this.exprId(id), value: t.text === 'true' };
      this.markExpr(e, t, t);
      return e;
    }
    const read = READ_FN[t.text];
    if (read !== undefined && this.toks[this.i + 1]?.text === '(') {
      this.next();
      this.next();
      const close = this.expect(')');
      const e: Expr = { k: 'read', id: this.exprId(id), as: read };
      this.markExpr(e, t, close);
      this.readAt.set(e.id, t);
      return e;
    }
    if (t.text === 'Math') return this.mathExpr(id, t);
    if (
      t.text === 'String' &&
      this.toks[this.i + 1]?.text === '.' &&
      this.toks[this.i + 2]?.text === 'fromCharCode'
    ) {
      this.next();
      this.next();
      this.next();
      this.expect('(');
      const arg = this.expression(id);
      const close = this.expect(')');
      const e: Expr = { k: 'chr', id: this.exprId(id), arg };
      this.markExpr(e, t, close);
      return e;
    }
    if (UNSUPPORTED_WORDS.has(t.text)) this.fail('unsupported', { what: t.text });
    this.next();
    if (this.at('(')) {
      // `Number(x)` and `String(x)` exist only to satisfy JavaScript; the value is the same.
      if (t.text === 'Number' || t.text === 'String') {
        this.next();
        const inner = this.expression(id);
        this.expect(')');
        return inner;
      }
      this.fail('unsupported', { what: `${t.text}()` });
    }
    const e: Expr = { k: 'var', id: this.exprId(id), name: t.text };
    this.markExpr(e, t, t);
    return e;
  }

  /**
   * `Math.trunc(a / b)` is how the JavaScript view writes whole-number division, and the only
   * whole-number division the IR has. `Math.floor` is what students reach for, but the two
   * disagree once a negative number is involved, so it is refused and named rather than quietly
   * run as something else.
   */
  private mathExpr(id: string, t: JsToken): Expr {
    const fn = this.toks[this.i + 2];
    if (this.toks[this.i + 1]?.text !== '.' || fn === undefined || fn.kind !== 'ident') {
      this.fail('unsupported', { what: 'Math' });
    }
    if (fn.text !== 'trunc') this.fail('unsupported', { what: `Math.${fn.text}` });
    this.next();
    this.next();
    this.next();
    this.expect('(');
    const arg = this.expression(id);
    const close = this.expect(')');
    if (arg.k !== 'arith' || arg.op !== '/') {
      this.fail('unsupported', { what: 'Math.trunc of something other than a division' });
    }
    const e: Expr = { ...arg, op: 'idiv' };
    this.markExpr(e, t, close);
    return e;
  }

  // ---------- after the parse ----------

  result(body: Stmt[]): {
    program: Program;
    lineMap: Record<string, LineRange>;
    headMap: Record<string, LineRange>;
    spans: Record<string, Span>;
  } {
    const settled = this.settle(body);
    this.checkReads(settled);
    const first = inferTypes({
      startId: 't:start',
      body: settled,
      variables: [],
      types: {},
      diagnostics: [],
    });
    // `new Array(n).fill(0)` is the same text for whole and decimal numbers, so the element type
    // is recovered from what the program stores — and the types are worked out again after that.
    this.retypeArrays(first);
    this.firstCharCodes(first);
    const program: Program = inferTypes({ ...first, types: {}, diagnostics: [] });
    const last = Object.values(this.lineMap).reduce((m, r) => Math.max(m, r[1]), 1);
    this.lineMap['t:start'] = [1, last];
    this.headMap['t:start'] = [1, 1];
    return { program, lineMap: this.lineMap, headMap: this.headMap, spans: this.spans };
  }

  /**
   * A read may only stand as the whole value of `x = readNumber()` — the rule the blocks enforce
   * with a `read-placement` diagnostic. In typed code the same mistake is a parse error, pointed
   * at the call, rather than a program that stops the moment it reaches it.
   */
  private checkReads(body: Stmt[]): void {
    const onItsOwn = new Set<string>();
    for (const s of walkStmts(body)) {
      // `x = readNumber()` and `a[i] = readNumber()` are both whole-value reads.
      if ((s.k === 'set' || s.k === 'setAt') && s.value.k === 'read') onItsOwn.add(s.value.id);
    }
    for (const s of walkStmts(body)) {
      for (const e of stmtExprs(s)) {
        for (const x of walkExpr(e)) {
          const at = x.k === 'read' && !onItsOwn.has(x.id) ? this.readAt.get(x.id) : undefined;
          if (at)
            this.failAt(at, 'unsupported', { what: `${at.text}() inside a larger expression` });
        }
      }
    }
  }

  /**
   * `new Array(n).fill(0)` is the same text for a list of whole numbers and one of decimals, so
   * the fill cannot tell them apart. What is put into the list can: a place that receives a
   * fraction makes it a list of decimals, exactly as the type checker decides for a variable.
   */
  private retypeArrays(program: Program): void {
    const fractional = new Set<string>();
    for (const s of walkStmts(program.body)) {
      if (s.k === 'setAt' && typeOf(s.value, program.types) === 'double') fractional.add(s.name);
    }
    // Mutating the tree we have just built ourselves; nothing else has seen it yet.
    for (const s of walkStmts(program.body)) {
      if (s.k !== 'newArray' || (s.elem !== 'int' && s.elem !== 'double')) continue;
      s.elem = fractional.has(s.name) ? 'double' : 'int';
    }
  }

  /**
   * `c.charCodeAt(0)` is the code of a character, but on a whole text `s.charCodeAt(0)` is the code
   * of its first character — which only the types can tell apart.
   */
  private firstCharCodes(program: Program): void {
    for (const s of walkStmts(program.body)) {
      for (const e of stmtExprs(s)) {
        for (const x of walkExpr(e)) {
          if (x.k !== 'code' || !this.charCodeAt0.has(x.id) || x.arg.k !== 'var') continue;
          if (program.types[x.arg.name] !== 'string') continue;
          const zero: Expr = { k: 'num', id: `${x.arg.id}/0`, value: 0 };
          x.arg = { k: 'at', id: `${x.arg.id}/at`, name: x.arg.name, index: zero, index2: null };
        }
      }
    }
  }

  /**
   * `+` adds numbers and joins text, and JavaScript declares no types, so the only way to tell
   * the two apart is to follow what flows into each variable. A variable only ever moves from
   * "number" to "text" here, never back, so the fixpoint is reached in a few rounds.
   */
  private textVars(body: Stmt[]): Set<string> {
    const text = new Set<string>();
    for (let round = 0; round < 20; round++) {
      const before = text.size;
      for (const s of walkStmts(body)) {
        if (s.k === 'set' && this.isText(s.value, text)) text.add(s.name);
      }
      if (text.size === before) break;
    }
    return text;
  }

  private isText(e: Expr, text: ReadonlySet<string>): boolean {
    switch (e.k) {
      case 'text':
      case 'join':
        return true;
      case 'read':
        return e.as === 'text';
      case 'var':
        return text.has(e.name);
      case 'at':
        // One character of a text is text too.
        return text.has(e.name);
      case 'chr':
      case 'case':
      case 'substr':
        return true;
      case 'arith':
        return e.op === '+' && (this.isText(e.left, text) || this.isText(e.right, text));
      default:
        return false;
    }
  }

  /**
   * Which names the text uses as a list (1) or as a table (2). Purely structural, because it has
   * to be: `a.length` is the size of a list, and `a[0].length` is the width of a table — but a
   * list of words writes that very same `a[0].length` for the length of one word. Only the rest
   * of the program says which, and it says it before any type is known.
   */
  private arrayRanks(body: Stmt[]): Map<string, 1 | 2> {
    const ranks = new Map<string, 1 | 2>();
    const note = (name: string, rank: 1 | 2) => {
      if ((ranks.get(name) ?? 0) < rank) ranks.set(name, rank);
    };
    for (const s of walkStmts(body)) {
      if (s.k === 'newArray') note(s.name, s.dims.length === 2 ? 2 : 1);
      if (s.k === 'setAt') note(s.name, s.index2 ? 2 : 1);
      for (const e of stmtExprs(s)) {
        for (const x of walkExpr(e)) if (x.k === 'at') note(x.name, x.index2 ? 2 : 1);
      }
    }
    return ranks;
  }

  /**
   * The two things a single statement cannot decide on its own: whether a `+` adds or joins, and
   * whether a `.length` counts letters or places. Both are settled once the whole program is in
   * hand, which is here.
   */
  private settle(body: Stmt[]): Stmt[] {
    const text = this.textVars(body);
    const ranks = this.arrayRanks(body);
    // `s[i]` on a text is one of its characters, not a place of a list: `s.length` stays a length.
    for (const name of text) ranks.delete(name);
    const fixExpr = (e: Expr): Expr => {
      switch (e.k) {
        case 'arith': {
          const left = fixExpr(e.left);
          const right = fixExpr(e.right);
          if (e.op === '+' && (this.isText(left, text) || this.isText(right, text))) {
            return { k: 'join', id: e.id, left, right };
          }
          return left === e.left && right === e.right ? e : { ...e, left, right };
        }
        case 'compare':
        case 'logic':
        case 'join':
          return { ...e, left: fixExpr(e.left), right: fixExpr(e.right) };
        case 'not':
        case 'code':
        case 'chr':
        case 'case':
          return { ...e, arg: fixExpr(e.arg) };
        case 'find':
          return { ...e, needle: fixExpr(e.needle), hay: fixExpr(e.hay) };
        case 'substr':
          return { ...e, arg: fixExpr(e.arg), from: fixExpr(e.from), len: fixExpr(e.len) };
        case 'length': {
          const arg = fixExpr(e.arg);
          if (arg.k === 'var' && ranks.has(arg.name)) {
            return { k: 'size', id: e.id, name: arg.name, dim: 1 };
          }
          if (arg.k === 'at' && arg.index2 === null && ranks.get(arg.name) === 2) {
            return { k: 'size', id: e.id, name: arg.name, dim: 2 };
          }
          return { ...e, arg };
        }
        case 'at':
          return {
            ...e,
            index: fixExpr(e.index),
            index2: e.index2 ? fixExpr(e.index2) : null,
          };
        default:
          return e;
      }
    };
    // Anything the IR grows later passes through untouched rather than silently losing a body.
    const fixStmts = (stmts: Stmt[]): Stmt[] =>
      stmts.map((s): Stmt => {
        switch (s.k) {
          case 'set':
            return { ...s, value: fixExpr(s.value) };
          case 'change':
            return { ...s, delta: fixExpr(s.delta) };
          case 'print':
            return { ...s, value: fixExpr(s.value) };
          case 'newArray': {
            const [rows, cols] = s.dims;
            const dims: [Expr] | [Expr, Expr] =
              cols === undefined ? [fixExpr(rows)] : [fixExpr(rows), fixExpr(cols)];
            return { ...s, dims };
          }
          case 'setAt':
            return {
              ...s,
              index: fixExpr(s.index),
              index2: s.index2 ? fixExpr(s.index2) : null,
              value: fixExpr(s.value),
            };
          case 'if':
            return {
              ...s,
              branches: s.branches.map((b) => ({ cond: fixExpr(b.cond), body: fixStmts(b.body) })),
              orElse: s.orElse ? fixStmts(s.orElse) : null,
            };
          case 'repeat':
            return { ...s, times: fixExpr(s.times), body: fixStmts(s.body) };
          case 'while':
            return { ...s, cond: fixExpr(s.cond), body: fixStmts(s.body) };
          case 'for':
            return { ...s, from: fixExpr(s.from), to: fixExpr(s.to), body: fixStmts(s.body) };
          default:
            return s;
        }
      });
    return fixStmts(body);
  }
}

/** Parses JavaScript text into the same Program IR the blocks produce. Never throws. */
export function parseJs(source: string): TextProgram {
  const empty = (errors: TextError[]): TextProgram => ({
    program: { startId: null, body: [], variables: [], types: {}, diagnostics: [] },
    code: { language: 'js', code: source, lineMap: {}, headMap: {} },
    spans: {},
    errors,
  });
  let parser: JsParser;
  try {
    parser = new JsParser(source, lexJs(source));
  } catch (e) {
    if (e instanceof JsLexError) return empty([e.info]);
    throw e;
  }
  try {
    const body = parser.parse();
    const { program, lineMap, headMap, spans } = parser.result(body);
    return { program, code: { language: 'js', code: source, lineMap, headMap }, spans, errors: [] };
  } catch (e) {
    if (e instanceof Fail) return empty([e.info]);
    if (e instanceof JsLexError) return empty([e.info]);
    throw e;
  }
}
